import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  FerryIngredient,
  FlightIngredient,
  RecipeIngredient,
  RecipeMainIngredient,
  StayIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  isFlightIngredient,
  isUnbookableTransportIngredient,
} from '@apps/main/modules/recipe/ingredients';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { FerryDetailsModalData } from '@apps/main/new-desktop/modules/ferryDetailsModal';
import type { FlightDetailsModalData } from '@apps/main/new-desktop/modules/flightDetailsModal';
import type {
  AddFlightsModalInitialRoute,
  AddFlightsModalRouteStopData,
  RouteData,
} from '@apps/main/new-desktop/modules/addFlightsModal';

import {
  isAvailableFerryIngredientResponse,
  isAvailableFlightIngredientResponse,
} from '@libs/search-service-smart-search';
import { TransportType } from '@libs/trip-planner-interfaces';
import type {
  CarrierStaticInfo,
  FerryIngredientResponse,
  FlightIngredientResponse,
  IngredientId,
  TransportAlternative,
  TransportLeg,
  TransportStaticInfo,
  TransportVehicle,
} from '@libs/trip-planner-interfaces';
import {
  assertValue,
  exhaustiveGuard,
  getHumanizedDuration,
} from '@libs/utils';
import { DateFormat, SemboDate } from '@libs/sembo-dates';

import type { HomeFlightData } from '../../../modules/airportSelector';
import type { ItineraryLocationSegment, LocationSegment } from '../models';
import { ItinerarySegmentType, ProductCardType } from '../models';

import {
  getAirportStaticInfo,
  getCarrierAndTransportationName,
  getCarrierStaticInfo,
  getRouteInfo,
  getVehicleInfo,
} from './ferryDetailsModalLogic';

const { t } = i18n.global;

export function getChangeButtonText(productType: ProductCardType): string {
  switch (productType) {
    case ProductCardType.Activity:
      return t('planner.change_activity');
    case ProductCardType.Ferry:
      return t('planner.change_ferry');
    case ProductCardType.Flight:
      return t('planner.change_flights');
    case ProductCardType.Accommodation:
      return t('planner.change_hotel');
    default:
      exhaustiveGuard(productType, 'the product card type is not defined');
  }
}

export function getHomeFlightData(
  recipe: RecipeMainIngredient[]
): HomeFlightData | null {
  if (!recipe.length) {
    return null;
  }

  const firstIngredient = recipe[0];

  assertValue(
    firstIngredient,
    'tripOverviewStoreLogic.ts > getHomeFlightData > firstIngredient'
  );

  const outboundFlight = isFlightIngredient(firstIngredient)
    ? firstIngredient
    : null;

  const lastIngredient = recipe[recipe.length - 1];

  assertValue(
    lastIngredient,
    'tripOverviewStoreLogic.ts > getHomeFlightData > lastIngredient'
  );

  const homeboundFlight = isFlightIngredient(lastIngredient)
    ? lastIngredient
    : null;

  if (outboundFlight) {
    if (
      !outboundFlight.fromPort ||
      !outboundFlight.fromPortText ||
      !outboundFlight.fromPortDisplayText
    ) {
      return null;
    }

    return {
      airport: {
        iata: outboundFlight.fromPort,
        name: outboundFlight.fromPortText,
        displayingText: outboundFlight.fromPortDisplayText,
      },
      outboundIngredientId: outboundFlight.ingredientId,
      homeboundIngredientId:
        homeboundFlight?.toPort === outboundFlight.fromPort
          ? homeboundFlight?.ingredientId
          : undefined,
    };
  }

  if (homeboundFlight) {
    if (
      !homeboundFlight.toPort ||
      !homeboundFlight.toPortText ||
      !homeboundFlight.toPortDisplayText
    ) {
      return null;
    }

    return {
      airport: {
        iata: homeboundFlight.toPort,
        name: homeboundFlight.toPortText,
        displayingText: homeboundFlight.toPortDisplayText,
      },
      homeboundIngredientId: homeboundFlight.ingredientId,
    };
  }

  return null;
}

export function getFerryDetails(
  ingredientResponse: FerryIngredientResponse,
  staticInfo: TransportStaticInfo | undefined,
  ticketType: string | undefined,
  ferryVehicleTypes: TransportVehicle[] | undefined
): FerryDetailsModalData | undefined {
  if (!isAvailableFerryIngredientResponse(ingredientResponse)) {
    return;
  }

  const leg = ingredientResponse.legs[ingredientResponse.legIndex];
  if (!leg) {
    return;
  }

  return {
    departureText: getAirportStaticInfo(
      ingredientResponse.departureIata,
      staticInfo
    )?.name,
    arrivalText: getAirportStaticInfo(
      ingredientResponse.arrivalIata,
      staticInfo
    )?.name,

    arrivalDate: ingredientResponse.arrivalDate,
    departureDate: ingredientResponse.departureDate,

    price: ingredientResponse.totalPrice,
    addToBasketToken: ingredientResponse.addToBasketToken,

    segment: leg.segments.map((segment) => ({
      carrier: getCarrierStaticInfo(segment.carrier, staticInfo),
      duration: segment.durationInMinutes,
      carrierAndTransportationName: getCarrierAndTransportationName(
        getCarrierStaticInfo(segment.carrier, staticInfo)?.name,
        segment.transportationName
      ),
      routeInfo: getRouteInfo(
        getVehicleInfo(ferryVehicleTypes, segment.vehicles),
        ticketType,
        segment.isCabinMandatory
      ),
    })),
  };
}

export function getFlightDetails(
  ingredientResponse: FlightIngredientResponse,
  staticInfo: TransportStaticInfo | undefined
): FlightDetailsModalData | undefined {
  if (!isAvailableFlightIngredientResponse(ingredientResponse)) {
    if (ingredientResponse.alternativeSuggestion) {
      const leg =
        ingredientResponse.alternativeSuggestion.legs[
          ingredientResponse.alternativeSuggestion.legIndex
        ];

      if (!leg) {
        return;
      }

      return {
        title: {
          fromAirport: getAirportStaticInfo(
            ingredientResponse.alternativeSuggestion.departureIata,
            staticInfo
          )?.name,
          toAirport: getAirportStaticInfo(
            ingredientResponse.alternativeSuggestion.arrivalIata,
            staticInfo
          )?.name,
        },

        segments: leg.segments.map((segment) => {
          const arrivalAirport = getAirportStaticInfo(
            segment.arrivalIata,
            staticInfo
          )?.name;
          const departureAirport = getAirportStaticInfo(
            segment.departureIata,
            staticInfo
          )?.name;
          return {
            arrivalDate: getTime(segment.arrivalDateTime),
            arrivalAirport,
            departureDate: getTime(segment.departureDateTime),
            departureAirport,
            carrier: getCarrierInfo(segment.carrier, staticInfo),
            operatingCarrier: getCarrierInfo(
              segment.operatingCarrier,
              staticInfo
            )?.name,
            flightNumber: segment.transportId,
            duration: getHumanizedDuration(
              segment.durationInMinutes ?? 0,
              'minute'
            ),
            stopover:
              segment.layoverTimeUntilNextSegmentInMinutes > 0
                ? getHumanizedDuration(
                    segment.layoverTimeUntilNextSegmentInMinutes,
                    'minute'
                  )
                : undefined,
          };
        }),
        price: ingredientResponse.alternativeSuggestion.totalPrice,
        arrivalDate: ingredientResponse.alternativeSuggestion.arrivalDate,
        departureDate: ingredientResponse.alternativeSuggestion.departureDate,
      };
    }
    return;
  }

  const leg = ingredientResponse.legs[ingredientResponse.legIndex];

  if (!leg) {
    return;
  }

  return {
    title: {
      fromAirport: getAirportStaticInfo(
        ingredientResponse.departureIata,
        staticInfo
      )?.name,
      toAirport: getAirportStaticInfo(
        ingredientResponse.arrivalIata,
        staticInfo
      )?.name,
    },

    segments: leg.segments.map((segment) => {
      const arrivalAirport = getAirportStaticInfo(
        segment.arrivalIata,
        staticInfo
      )?.name;
      const departureAirport = getAirportStaticInfo(
        segment.departureIata,
        staticInfo
      )?.name;
      return {
        arrivalDate: getTime(segment.arrivalDateTime),
        arrivalAirport,
        departureDate: getTime(segment.departureDateTime),
        departureAirport,
        carrier: getCarrierInfo(segment.carrier, staticInfo),
        operatingCarrier: getCarrierInfo(segment.operatingCarrier, staticInfo)
          ?.name,
        flightNumber: segment.transportId,
        duration: getHumanizedDuration(
          segment.durationInMinutes ?? 0,
          'minute'
        ),
        stopover:
          segment.layoverTimeUntilNextSegmentInMinutes > 0
            ? getHumanizedDuration(
                segment.layoverTimeUntilNextSegmentInMinutes,
                'minute'
              )
            : undefined,
      };
    }),
    price: ingredientResponse.totalPrice,
    arrivalDate: ingredientResponse.arrivalDate,
    departureDate: ingredientResponse.departureDate,
  };
}

function getTime(date: Date): string {
  const semboDate = new SemboDate(date);
  return semboDate.format(DateFormat.Time);
}

function getCarrierInfo(
  carrierCode: string,
  staticInfo: TransportStaticInfo | undefined
): CarrierStaticInfo | undefined {
  if (!staticInfo?.carriers) {
    return;
  }

  const carrier = staticInfo.carriers[carrierCode];

  return carrier;
}

export function updateFlightIngredientsWithAlternativeSuggestion(
  transportIngredientId: IngredientId,
  alternativeTransport: TransportAlternative,
  getTransportIngredient: (
    transportIngredientId: IngredientId
  ) => FlightIngredient | FerryIngredient
): void {
  const alternativeFlightResponseLeg =
    alternativeTransport.legs[alternativeTransport.legIndex];

  assertValue(alternativeFlightResponseLeg);

  const flightIngredient = getTransportIngredient(transportIngredientId);

  flightIngredient.transportIds = getAllTransportIdsFromLeg(
    alternativeFlightResponseLeg
  );
}

function getAllTransportIdsFromLeg(transportLeg: TransportLeg): string[] {
  return transportLeg.segments.flatMap((segment) => segment.transportId);
}

export function getAutocompleteTypeFromLocationSegment(
  segment: ItineraryLocationSegment
): AutoCompleteType.StartingPosition | AutoCompleteType.EndingPosition {
  return segment.segmentType === ItinerarySegmentType.StartLocation
    ? AutoCompleteType.StartingPosition
    : AutoCompleteType.EndingPosition;
}

export function getSearchTextFromLocationSegment(
  locationSegment: LocationSegment
): string {
  if (
    locationSegment.name === t('planner.your_position') ||
    locationSegment.name === t('planner.your_destination')
  ) {
    return '';
  }

  if (locationSegment.country) {
    return `${locationSegment.name}, ${locationSegment.country}`;
  }

  return locationSegment.name;
}

export function getSearchTextFromPolygon(polygonId: number): string {
  return `polygon-id:${polygonId}`;
}

export function getPreviousRouteData(
  currentStay: StayIngredient,
  currentStayHotelMainPolygonId?: number,
  previousStay?: StayIngredient,
  previousIngredient?: RecipeIngredient,
  previousStayHotelMainPolygonId?: number,
  homeFlightData?: HomeFlightData | null
): RouteData {
  const hasPreviousFlight =
    previousIngredient && isFlightIngredient(previousIngredient);

  const currentStayHotelSearchText: string | undefined =
    currentStayHotelMainPolygonId
      ? getSearchTextFromPolygon(currentStayHotelMainPolygonId)
      : undefined;

  const previousStayHotelSearchText: string | undefined =
    previousStayHotelMainPolygonId
      ? getSearchTextFromPolygon(previousStayHotelMainPolygonId)
      : undefined;

  return {
    from: hasPreviousFlight
      ? getRouteDataFromTransportIngredient(previousIngredient, 'from')
      : previousStay
        ? getStayRouteData(previousStay, previousStayHotelSearchText)
        : homeFlightData
          ? getHomeFlightRouteData(homeFlightData)
          : undefined,
    to: hasPreviousFlight
      ? getRouteDataFromTransportIngredient(previousIngredient, 'to')
      : getStayRouteData(currentStay, currentStayHotelSearchText),
  };
}

export function getRouteDataFromTransportIngredient(
  ingredient: FlightIngredient,
  direction: 'from' | 'to'
): AddFlightsModalRouteStopData {
  return {
    displayText:
      direction === 'from'
        ? (ingredient.fromPortDisplayText ?? '')
        : (ingredient.toPortDisplayText ?? ''),
    searchText:
      direction === 'from'
        ? (ingredient.fromPortText ?? '')
        : (ingredient.toPortText ?? ''),
    iataCode:
      direction === 'from'
        ? ingredient.fromPort || ''
        : ingredient.toPort || '',
  };
}

export function getStayRouteData(
  stay: StayIngredient,
  searchText?: string
): AddFlightsModalRouteStopData {
  return {
    displayText: stay.displayText,
    searchText: searchText || stay.getSearchText(),
  };
}

export function getNextRouteData(
  currentStay: StayIngredient,
  nextIngredient?: RecipeMainIngredient,
  currentStayHotelMainPolygonId?: number
): RouteData {
  const hasNextFlight = nextIngredient && isFlightIngredient(nextIngredient);

  const currentStayHotelSearchText: string | undefined =
    currentStayHotelMainPolygonId
      ? getSearchTextFromPolygon(currentStayHotelMainPolygonId)
      : undefined;

  return {
    from: hasNextFlight
      ? getRouteDataFromTransportIngredient(nextIngredient, 'from')
      : getStayRouteData(currentStay, currentStayHotelSearchText),
    to: hasNextFlight
      ? getRouteDataFromTransportIngredient(nextIngredient, 'to')
      : undefined,
  };
}

export function buildFlightRoutesFromStays(
  routes: AddFlightsModalInitialRoute[],
  previousRouteData: RouteData,
  nextRouteData: RouteData,
  currentStay: StayIngredient,
  isLastStay: boolean,
  nextIngredient?: RecipeMainIngredient,
  previousIngredient?: RecipeIngredient
): void {
  const isPreviousFlightTransport =
    !!previousIngredient && isFlightIngredient(previousIngredient);

  const shouldEnablePreviousFlight =
    !previousIngredient ||
    (isUnbookableTransportIngredient(previousIngredient) &&
      previousIngredient.ownTransportType === TransportType.None);

  const isEnabled = shouldEnablePreviousFlight || isPreviousFlightTransport;
  routes.push({
    from: previousRouteData.from,
    to: previousRouteData.to,
    ingredientId: currentStay.ingredientId,
    isLast: false,
    numberOfNights: currentStay.numberOfNights,
    isEnabled,
    isDirectFlight:
      isPreviousFlightTransport &&
      previousIngredient.filters.maxNumberOfStops === 0,
  });

  if (isLastStay) {
    const isLastFlightTransport =
      !!nextIngredient && isFlightIngredient(nextIngredient);

    const shouldEnableLastFlight =
      !nextIngredient ||
      (isUnbookableTransportIngredient(nextIngredient) &&
        nextIngredient.ownTransportType === TransportType.None);

    const isLastEnabled = shouldEnableLastFlight || isLastFlightTransport;
    routes.push({
      from: nextRouteData.from,
      to: nextRouteData.to,
      ingredientId: currentStay.ingredientId,
      isLast: true,
      numberOfNights: currentStay.numberOfNights,
      isEnabled: isLastEnabled,
      isDirectFlight:
        isLastFlightTransport && nextIngredient.filters.maxNumberOfStops === 0,
    });
  }
}

function getHomeFlightRouteData(
  homeFlightData: HomeFlightData
): AddFlightsModalRouteStopData {
  return {
    displayText: homeFlightData.airport.displayingText || '',
    searchText: homeFlightData.airport.name || '',
    iataCode: homeFlightData.airport.iata || '',
  };
}
